/**
 * Pure helpers for workflow node model slots.
 *
 * The backend serves its slot table at GET /workflows/model-slots (built
 * from MODEL_SLOT_RULES): which agent types have which model slot, which
 * config field holds a node's choice, and the model each slot inherits
 * from the workflow's profile, worked out as a run works it out.  The
 * builder reads that payload through these helpers, so it never hard-codes
 * which node types use which models.  See
 * docs/features/WORKFLOW_NODE_MODELS.md.
 */

/**
 * Builder copy per kind of slot.  A slot without an entry still gets a
 * picker, with a generic label (see slotCopy).
 */
export const SLOT_COPY = {
  extraction: {
    label: 'Model',
    hint: "The model for this node's extraction call.",
  },
  summarizer: {
    label: 'Summarizer model',
    hint: "Merges this node's sub-agent answers into one.",
  },
};

/** How the builder names each inherited source, as Settings does. */
const SOURCE_LABELS = {
  main_model: 'main model',
  summarizer_model: 'Summarizer Model',
};

/**
 * Config fields an agent node uses for its sub-agents.  A single-call node
 * (one whose slot is its whole model, like the Extractor) has none.
 */
export const SUB_AGENT_FIELDS = [
  'sub_agents',
  'provider',
  'model',
  'config_index',
];

/**
 * Return the slots a node of `agentType` has.
 *
 * @param {Array|null} modelSlots - The served slot table, or null while
 *   loading.
 * @param {string} agentType - The node's agent type.
 * @returns {Array} Matching slot entries, in table order.
 */
export function slotsForAgentType(modelSlots, agentType) {
  return (modelSlots || []).filter(
    (slot) => Array.isArray(slot.agent_types)
      && slot.agent_types.includes(agentType),
  );
}

/**
 * Whether nodes of `agentType` make a single model call, so they have no
 * sub-agents to override.
 *
 * @param {Array|null} modelSlots - The served slot table.
 * @param {string} agentType - The node's agent type.
 * @returns {boolean}
 */
export function isSingleCallAgentType(modelSlots, agentType) {
  return slotsForAgentType(modelSlots, agentType).some(
    (slot) => slot.node_model,
  );
}

/**
 * Return the label and hint for a slot's picker.
 *
 * @param {string} slot - The slot's name (e.g. "summarizer").
 * @returns {{label: string, hint: string}}
 */
export function slotCopy(slot) {
  if (SLOT_COPY[slot]) return SLOT_COPY[slot];
  const name = String(slot || 'node');
  return {
    label: `${name.charAt(0).toUpperCase()}${name.slice(1)} model`,
    hint: '',
  };
}

/**
 * Label the "use the profile" option with the model it resolves to.
 *
 * @param {object|undefined} defaultChoice - The slot's served `default`
 *   ({source, provider, model}).
 * @returns {string} e.g. "Profile default (main model: claude-opus-4-7)".
 */
export function defaultChoiceLabel(defaultChoice) {
  if (!defaultChoice || !defaultChoice.model) return 'Profile default';
  if (defaultChoice.source === 'platform_default') {
    return `Profile default (Stackform: ${defaultChoice.model})`;
  }
  const source = SOURCE_LABELS[defaultChoice.source] || 'profile';
  return `Profile default (${source}: ${defaultChoice.model})`;
}

/**
 * Fit a node config to a new agent type, as the backend does on load.
 *
 * Drops the slot fields the new type doesn't have, and, for a
 * single-call type, the sub-agent fields too, so nothing the user can't
 * see in the form is saved or carried over.
 *
 * @param {object} config - The node config.
 * @param {string} agentType - The new agent type.
 * @param {Array|null} modelSlots - The served slot table.
 * @returns {object} A new config.
 */
export function fitConfigToAgentType(config, agentType, modelSlots) {
  const fitted = { ...config };
  const keep = new Set(
    slotsForAgentType(modelSlots, agentType).map((slot) => slot.field),
  );
  (modelSlots || []).forEach((slot) => {
    if (!keep.has(slot.field)) delete fitted[slot.field];
  });
  if (isSingleCallAgentType(modelSlots, agentType)) {
    SUB_AGENT_FIELDS.forEach((field) => {
      delete fitted[field];
    });
  }
  return fitted;
}
